import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { articleContents } from "@/mocks/articleContents";
import { articleList } from "@/mocks/articles";
import { mainSiteUrl } from "@/mocks/categories";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://basetaosheet.top";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [openFaqIdx, setOpenFaqIdx] = useState<number | null>(null);

  // Scroll to top on article open
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articleContents.find((a) => a.slug === slug);
  const articleMeta = articleList.find((a) => a.slug === slug);

  // Get 3 related articles (excluding current)
  const relatedArticles = articleList
    .filter((a) => a.slug !== slug)
    .slice(0, 3);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center px-4">
            <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-2xl bg-background-100">
              <i className="ri-question-line text-3xl text-foreground-400"></i>
            </span>
            <h1 className="font-heading text-2xl font-semibold text-foreground-900 mb-2">Article Not Found</h1>
            <p className="text-sm text-foreground-600 mb-6">The article you are looking for does not exist yet.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer text-sm whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Homepage
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Build JSON-LD
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "author": {
      "@type": "Organization",
      "name": article.author,
    },
    "dateModified": article.updatedDate,
    "datePublished": article.date || article.updatedDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": siteUrl + "/article/" + slug,
    },
    "description": articleMeta?.excerpt || article.title,
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": siteUrl + "/",
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": article.category,
        "item": siteUrl + "/",
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": article.title,
        "item": siteUrl + "/article/" + slug,
      },
    ],
  };

  let faqCounter = 0;

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <Navbar />
      <main>
        {/* Article Header */}
        <header className="pt-24 pb-10 md:pt-28 md:pb-14 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-sm"></i>
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">{article.category}</Link>
              <i className="ri-arrow-right-s-line text-sm"></i>
              <span className="text-foreground-700 truncate max-w-[200px]">{article.title}</span>
            </nav>

            <span className="inline-block px-3 py-1 bg-accent-50 text-accent-700 text-xs font-medium rounded-full mb-4">
              {article.category}
            </span>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span>By {article.author}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span>Updated: {article.updatedDate}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span>{article.readTime}</span>
            </div>
          </div>
        </header>

        {/* Article Content */}
        <article className="py-10 md:py-14">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="prose-custom space-y-6">
              {article.sections.map((section, idx) => {
                if (section.type === "heading") {
                  return (
                    <h2 key={idx} className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 pt-4 pb-1">
                      {section.content}
                    </h2>
                  );
                }

                if (section.type === "introduction") {
                  return (
                    <p key={idx} className="text-base md:text-lg text-foreground-700 leading-relaxed font-medium">
                      {section.content}
                    </p>
                  );
                }

                if (section.type === "paragraph") {
                  return (
                    <p key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      {section.content}
                    </p>
                  );
                }

                if (section.type === "list" && section.items) {
                  return (
                    <ul key={idx} className="space-y-3 pl-1">
                      {section.items.map((item, i) => (
                        <li key={i} className="flex gap-3 text-sm md:text-base text-foreground-700 leading-relaxed">
                          <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center mt-0.5">
                            <i className="ri-check-line text-accent-600"></i>
                          </span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  );
                }

                if (section.type === "faq" && section.items) {
                  return (
                    <div key={idx} className="space-y-3 pt-4">
                      {section.items.map((faqItem, i) => {
                        const currentFaqIdx = faqCounter++;
                        return (
                          <div key={i} className="border border-background-200/70 rounded-xl overflow-hidden">
                            <button
                              onClick={() => setOpenFaqIdx(openFaqIdx === currentFaqIdx ? null : currentFaqIdx)}
                              className="w-full flex items-center justify-between p-4 text-left cursor-pointer hover:bg-background-100 transition-colors"
                            >
                              <h3 className="text-sm font-medium text-foreground-900 pr-4">
                                {faqItem.question}
                              </h3>
                              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                                <i
                                  className={`text-lg text-foreground-500 transition-transform duration-200 ${
                                    openFaqIdx === currentFaqIdx ? "ri-subtract-line" : "ri-add-line"
                                  }`}
                                ></i>
                              </span>
                            </button>
                            {openFaqIdx === currentFaqIdx && (
                              <div className="px-4 pb-4">
                                <p className="text-sm text-foreground-600 leading-relaxed">
                                  {faqItem.answer}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                return null;
              })}
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <div className="mt-14 pt-10 border-t border-background-200/70">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">
                  Related Articles
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all cursor-pointer"
                    >
                      <div className="h-32 overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <span className="text-xs text-accent-700 font-medium">{ra.category}</span>
                        <h4 className="text-sm font-semibold text-foreground-900 mt-1 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {ra.title}
                        </h4>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Article footer CTA */}
            <div className="mt-14 p-6 md:p-8 bg-background-100 border border-background-200/70 rounded-2xl text-center">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                Ready to Start Using BaseTao Spreadsheet?
              </h3>
              <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
                Apply everything you have learned and streamline your international shopping workflow today.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/"
                  className="px-6 py-2.5 border border-background-300 text-foreground-700 font-medium rounded-lg hover:border-background-400 transition-all cursor-pointer text-sm whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Homepage
                </Link>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-2.5 bg-primary-500 text-background-50 font-semibold rounded-lg hover:bg-primary-600 transition-all cursor-pointer text-sm whitespace-nowrap"
                >
                  Visit BaseTao Spreadsheet
                  <i className="ri-external-link-line ml-2"></i>
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}